/**
 * Snapping and distance measurement utility for Ticket & Badge Designer.
 * Inspired by Zoho Backstage, Figma, and Floor-Planner snapping.
 * Supports full 3-axis alignment guides (Left, Center, Right & Top, Center, Bottom).
 * All guides are strictly confined within canvas boundaries and zoom-adaptive.
 */

export const getTicketHelperLines = (
  node,
  siblings,
  canvasBounds = { width: 900, height: 600 },
  threshold = 8,
) => {
  const nodeWidth = Number(node.width) || 0;
  const nodeHeight = Number(node.height) || 0;
  const canvasWidth = Number(canvasBounds.width) || 900;
  const canvasHeight = Number(canvasBounds.height) || 600;

  const clampX = (v) => Math.max(1, Math.min(canvasWidth - 1, Math.round(v)));
  const clampY = (v) => Math.max(1, Math.min(canvasHeight - 1, Math.round(v)));

  const nodeRect = {
    left: node.x,
    right: node.x + nodeWidth,
    top: node.y,
    bottom: node.y + nodeHeight,
    centerX: node.x + nodeWidth / 2,
    centerY: node.y + nodeHeight / 2,
  };

  // Collect candidate target positions for X (Vertical guides)
  // 1. Canvas Left, Center, Right
  const targetXList = [
    { value: 0, type: "canvas-left" },
    { value: canvasWidth / 2, type: "canvas-center" },
    { value: canvasWidth, type: "canvas-right" },
  ];

  // Collect candidate target positions for Y (Horizontal guides)
  // 1. Canvas Top, Center, Bottom
  const targetYList = [
    { value: 0, type: "canvas-top" },
    { value: canvasHeight / 2, type: "canvas-center" },
    { value: canvasHeight, type: "canvas-bottom" },
  ];

  // 2. Sibling elements targets (Left, Center, Right & Top, Center, Bottom)
  for (const other of siblings) {
    if (!other || other.id === node.id) continue;
    const otherWidth = Number(other.width) || 0;
    const otherHeight = Number(other.height) || 0;

    targetXList.push(
      { value: other.x, type: "other-left", otherId: other.id },
      { value: other.x + otherWidth / 2, type: "other-center", otherId: other.id },
      { value: other.x + otherWidth, type: "other-right", otherId: other.id },
    );

    targetYList.push(
      { value: other.y, type: "other-top", otherId: other.id },
      { value: other.y + otherHeight / 2, type: "other-center", otherId: other.id },
      { value: other.y + otherHeight, type: "other-bottom", otherId: other.id },
    );
  }

  // ========================================================
  // EVALUATE 3 VERTICAL AXES (Along horizontal width):
  // 1. Center (nodeRect.centerX) - Priority alignment
  // 2. Left   (nodeRect.left)
  // 3. Right  (nodeRect.right)
  // ========================================================
  let bestSnapX = null;
  let minDiffX = threshold;
  let snappedAxisX = null;

  // Check Center axis first (e.g. canvas center or sibling center)
  for (const target of targetXList) {
    const diff = Math.abs(nodeRect.centerX - target.value);
    if (diff <= threshold && diff < minDiffX) {
      minDiffX = diff;
      bestSnapX = target.value - nodeWidth / 2;
      snappedAxisX = "center";
    }
  }

  // Check Left edge
  for (const target of targetXList) {
    const diff = Math.abs(nodeRect.left - target.value);
    if (diff <= threshold && diff < minDiffX) {
      minDiffX = diff;
      bestSnapX = target.value;
      snappedAxisX = "left";
    }
  }

  // Check Right edge
  for (const target of targetXList) {
    const diff = Math.abs(nodeRect.right - target.value);
    if (diff <= threshold && diff < minDiffX) {
      minDiffX = diff;
      bestSnapX = target.value - nodeWidth;
      snappedAxisX = "right";
    }
  }

  // ========================================================
  // EVALUATE 3 HORIZONTAL AXES (Along vertical height):
  // 1. Center (nodeRect.centerY)
  // 2. Top    (nodeRect.top)
  // 3. Bottom (nodeRect.bottom)
  // ========================================================
  let bestSnapY = null;
  let minDiffY = threshold;
  let snappedAxisY = null;

  // Check Center axis
  for (const target of targetYList) {
    const diff = Math.abs(nodeRect.centerY - target.value);
    if (diff <= threshold && diff < minDiffY) {
      minDiffY = diff;
      bestSnapY = target.value - nodeHeight / 2;
      snappedAxisY = "center";
    }
  }

  // Check Top edge
  for (const target of targetYList) {
    const diff = Math.abs(nodeRect.top - target.value);
    if (diff <= threshold && diff < minDiffY) {
      minDiffY = diff;
      bestSnapY = target.value;
      snappedAxisY = "top";
    }
  }

  // Check Bottom edge
  for (const target of targetYList) {
    const diff = Math.abs(nodeRect.bottom - target.value);
    if (diff <= threshold && diff < minDiffY) {
      minDiffY = diff;
      bestSnapY = target.value - nodeHeight;
      snappedAxisY = "bottom";
    }
  }

  const finalX = bestSnapX !== null ? bestSnapX : node.x;
  const finalY = bestSnapY !== null ? bestSnapY : node.y;

  // 3 Vertical guide lines across horizontal size: Left, Center, Right
  const curLeft = clampX(finalX);
  const curCenterX = clampX(finalX + nodeWidth / 2);
  const curRight = clampX(finalX + nodeWidth);

  // 3 Horizontal guide lines across vertical size: Top, Center, Bottom
  const curTop = clampY(finalY);
  const curCenterY = clampY(finalY + nodeHeight / 2);
  const curBottom = clampY(finalY + nodeHeight);

  // Always supply the 3 horizontal size guides (Left, Center, Right)
  // and 3 vertical size guides (Top, Center, Bottom) with active snap highlight
  const lines = [
    // 3 Vertical Lines across the horizontal width
    {
      id: "v-left",
      x1: curLeft,
      y1: 0,
      x2: curLeft,
      y2: canvasHeight,
      role: "left",
      isAligned: snappedAxisX === "left",
    },
    {
      id: "v-center",
      x1: curCenterX,
      y1: 0,
      x2: curCenterX,
      y2: canvasHeight,
      role: "center",
      isAligned: snappedAxisX === "center",
    },
    {
      id: "v-right",
      x1: curRight,
      y1: 0,
      x2: curRight,
      y2: canvasHeight,
      role: "right",
      isAligned: snappedAxisX === "right",
    },
    // 3 Horizontal Lines across the vertical height
    {
      id: "h-top",
      x1: 0,
      y1: curTop,
      x2: canvasWidth,
      y2: curTop,
      role: "top",
      isAligned: snappedAxisY === "top",
    },
    {
      id: "h-center",
      x1: 0,
      y1: curCenterY,
      x2: canvasWidth,
      y2: curCenterY,
      role: "center",
      isAligned: snappedAxisY === "center",
    },
    {
      id: "h-bottom",
      x1: 0,
      y1: curBottom,
      x2: canvasWidth,
      y2: curBottom,
      role: "bottom",
      isAligned: snappedAxisY === "bottom",
    },
  ];

  return {
    snapX: bestSnapX,
    snapY: bestSnapY,
    snappedAxisX,
    snappedAxisY,
    lines,
  };
};

/**
 * Calculates clean pixel distance measurements between the active node and neighboring elements.
 * Exactly like Zoho Backstage: shows only direct gap between aligned elements without clutter.
 */
export const getDistanceMeasurements = (
  node,
  siblings,
  canvasBounds = { width: 900, height: 600 },
) => {
  const nodeWidth = Number(node.width) || 0;
  const nodeHeight = Number(node.height) || 0;
  const canvasWidth = Number(canvasBounds.width) || 900;
  const canvasHeight = Number(canvasBounds.height) || 600;

  const nodeRect = {
    left: node.x,
    right: node.x + nodeWidth,
    top: node.y,
    bottom: node.y + nodeHeight,
    centerX: node.x + nodeWidth / 2,
    centerY: node.y + nodeHeight / 2,
  };

  const measurements = [];

  // Distances to neighboring elements sharing alignment/projection
  for (const other of siblings) {
    if (!other || other.id === node.id) continue;

    const otherWidth = Number(other.width) || 0;
    const otherHeight = Number(other.height) || 0;

    const otherRect = {
      left: other.x,
      right: other.x + otherWidth,
      top: other.y,
      bottom: other.y + otherHeight,
      centerX: other.x + otherWidth / 2,
      centerY: other.y + otherHeight / 2,
    };

    const hasHorizontalOverlap =
      nodeRect.left < otherRect.right + 40 && nodeRect.right > otherRect.left - 40;
    const hasVerticalOverlap =
      nodeRect.top < otherRect.bottom + 40 && nodeRect.bottom > otherRect.top - 40;

    // Vertical Distance (node above other)
    if (hasHorizontalOverlap && nodeRect.bottom < otherRect.top) {
      const gap = otherRect.top - nodeRect.bottom;
      if (gap >= 1 && gap <= 350) {
        measurements.push({
          id: `v-${other.id}-below`,
          orientation: "v",
          value: Math.round(gap),
          gap,
          x: nodeRect.centerX + 14,
          y: nodeRect.bottom + gap / 2,
        });
      }
    }

    // Vertical Distance (node below other)
    if (hasHorizontalOverlap && otherRect.bottom < nodeRect.top) {
      const gap = nodeRect.top - otherRect.bottom;
      if (gap >= 1 && gap <= 350) {
        measurements.push({
          id: `v-${other.id}-above`,
          orientation: "v",
          value: Math.round(gap),
          gap,
          x: nodeRect.centerX + 14,
          y: otherRect.bottom + gap / 2,
        });
      }
    }

    // Horizontal Distance (node to left of other)
    if (hasVerticalOverlap && nodeRect.right < otherRect.left) {
      const gap = otherRect.left - nodeRect.right;
      if (gap >= 1 && gap <= 350) {
        measurements.push({
          id: `h-${other.id}-right`,
          orientation: "h",
          value: Math.round(gap),
          gap,
          x: nodeRect.right + gap / 2,
          y: nodeRect.centerY - 10,
        });
      }
    }

    // Horizontal Distance (node to right of other)
    if (hasVerticalOverlap && otherRect.right < nodeRect.left) {
      const gap = nodeRect.left - otherRect.right;
      if (gap >= 1 && gap <= 350) {
        measurements.push({
          id: `h-${other.id}-left`,
          orientation: "h",
          value: Math.round(gap),
          gap,
          x: otherRect.right + gap / 2,
          y: nodeRect.centerY - 10,
        });
      }
    }
  }

  // Sort by smallest gap to prioritize the immediate neighbor
  measurements.sort((a, b) => a.gap - b.gap);

  // Return at most the 2 closest neighbor gaps
  if (measurements.length > 0) {
    return measurements.slice(0, 2);
  }

  // Only if no neighbor gap exists and element is near canvas border (<= 40px), show single margin
  if (nodeRect.top > 0 && nodeRect.top <= 40) {
    measurements.push({
      id: "canvas-top",
      orientation: "v",
      value: Math.round(nodeRect.top),
      x: nodeRect.centerX + 14,
      y: nodeRect.top / 2,
    });
  } else if (canvasHeight - nodeRect.bottom > 0 && canvasHeight - nodeRect.bottom <= 40) {
    measurements.push({
      id: "canvas-bottom",
      orientation: "v",
      value: Math.round(canvasHeight - nodeRect.bottom),
      x: nodeRect.centerX + 14,
      y: nodeRect.bottom + (canvasHeight - nodeRect.bottom) / 2,
    });
  }

  return measurements.slice(0, 1);
};
